import React, { useMemo } from 'react'
import { TfiWallet } from 'react-icons/tfi'
import { NavLink } from 'react-router-dom'

import { Alert } from '@vtex/shoreline'

import { ButtonNavLink } from '~/components/Buttons'
import { Spinner } from '~/components/Spinner'
import { Badge } from '~/components/UI/Badge'
import { formatMoney } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useFetchSWR from '~/hooks/useFetchSWR'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { checkingAccount } from '../../services/industry'

import * as S from './styles'

type CheckingAccountItem = GetCheckingAccountResponse[number]

type BalanceProps = {
  id: string
  idAccountType: AccountTypes
  showWalletLink?: boolean
  showRechargeLink?: boolean
  showZeroAlert?: boolean
  /**
   * Ads network + PI flow (CampaignForm create):
   * - `undefined` — legacy balance by publisher/advertiser (non ads network)
   * - `null` — ads network without PI; hide until a PI is linked
   * - `string` — fetch `GET /checking-account/{id}` (PI wallet)
   */
  checkingAccountId?: string | null
}

const BalanceByPublisher: React.FC<BalanceProps> = ({
  id,
  idAccountType,
  showWalletLink = true,
  showRechargeLink = true,
  showZeroAlert = false,
  checkingAccountId
}) => {
  const { t } = useAPPTranslation()
  const { hasRecharge } = useAppInfo()
  const shouldHide = checkingAccountId === null
  const isByCheckingAccount = typeof checkingAccountId === 'string'

  // O BFF casa ID do token com ID enviado para trazer a carteira
  const paramKeyName = idAccountType === 'grocery' ? 'advertiser' : 'publisher'

  const paramKey = `${paramKeyName}_id` as 'advertiser_id' | 'publisher_id'

  const balanceURL = useMemo(() => {
    if (isByCheckingAccount) {
      // account_info=true is required by the API for GET by id
      // (omitting it causes: Cannot read properties of undefined (reading 'publisher_id'))
      return `${checkingAccount.baseURL}/${checkingAccountId}?account_info=true`
    }

    return id
      ? `${checkingAccount.baseURL}?${paramKey}=${id}&count=false`
      : `${checkingAccount.baseURL}?count=false`
  }, [checkingAccountId, id, isByCheckingAccount, paramKey])

  const { data, isValidating, error } = useFetchSWR<
    GetCheckingAccountResponse | CheckingAccountItem
  >({
    url: balanceURL,
    shouldFetch: !shouldHide,
    refreshInterval: 60000,
    revalidateOnFocus: true
  })

  const balanceValue = useMemo(() => {
    if (isByCheckingAccount) {
      const item = data as CheckingAccountItem | undefined
      return Number(item?.balance) || 0
    }

    const list = data as GetCheckingAccountResponse | undefined
    const items = id ? list?.filter(item => item[paramKey] === id) : list
    return (
      items?.reduce((sum, item) => sum + (Number(item.balance) || 0), 0) ?? 0
    )
  }, [data, id, isByCheckingAccount, paramKey])

  const balance = useMemo(() => formatMoney(balanceValue), [balanceValue])

  const walletId = useMemo(() => {
    if (isByCheckingAccount) {
      return checkingAccountId
    }

    const list = data as GetCheckingAccountResponse | undefined
    const newData = id ? list?.find(item => item[paramKey] === id) : list?.[0]
    return newData?.id
  }, [checkingAccountId, data, id, isByCheckingAccount, paramKey])

  if (shouldHide) {
    return null
  }

  return (
    <S.Container>
      {error && (
        <Badge iconLeft={<TfiWallet />} style={{ height: '22px' }}>
          {t('rm:wallet.balance.loading.error')}
        </Badge>
      )}

      {!error && (
        <>
          <NavLink to={`/retail-media/wallet/${walletId}?rmid=${id}`}>
            <Badge
              iconLeft={<TfiWallet />}
              style={{ height: '22px' }}
              {...(!showWalletLink && { className: 'me-0' })}
            >
              {t('rm:wallet.campaign.walletBalance')}: {balance}{' '}
              {isValidating && <Spinner />}
            </Badge>
          </NavLink>

          {showWalletLink && (
            <ButtonNavLink
              to={`/retail-media/wallet/${walletId}?rmid=${id}`}
              size="xsmall"
              text={t('rm:wallet.campaign.walletLink')}
              template="transparentPrimary"
            />
          )}

          {hasRecharge && showRechargeLink && idAccountType === 'industry' && (
            <ButtonNavLink
              to={`/retail-media/wallet/${walletId}/recharge?rmid=${id}`}
              size="xsmall"
              text={t('rm:wallet.table.actions.addCredit')}
              template="transparentPrimary"
              target="_blank"
              rel="noreferrer"
            />
          )}
        </>
      )}

      {showZeroAlert && !isNaN(balanceValue) && balanceValue <= 0 && (
        <footer style={{ flex: '1 1 100%' }}>
          <Alert variant="critical">
            {t('rm:wallet.balance.empty')}
            <ButtonNavLink
              template="tertiary"
              to={`/retail-media/wallet/${walletId}?rmid=${id}`}
            >
              {t('rm:wallet.balance.accessWallet')}
            </ButtonNavLink>
          </Alert>
        </footer>
      )}
    </S.Container>
  )
}

export default BalanceByPublisher
